import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, ChevronLeft, ChevronRight, Check, ThumbsDown, Pencil, Trash2, Copy, Loader2, Undo2,
  Type, Image as ImageIcon, Square, Shapes, GitCompare, Tags, Send, Globe, EyeOff,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageThumbnailPreview from '../../../components/Editor/PageThumbnailPreview';
import { similarTemplates } from './reviewApi';
import {
  STATUS_BADGE, STATUS_LABEL, canApprove, canReject, canRevise, canPublish, canUnpublish,
  pagesOf, elementsOf, canvasOf, formatDate,
} from './reviewConstants';

/**
 * Full-screen review of one template.
 *
 * Left: the design, rendered live at the largest size the window allows.
 * Right: what a reviewer needs to decide — metadata, the engine's variation
 * record, every editable element, and the templates this one most resembles.
 *
 * Built for going through hundreds: ← / → move through the current list,
 * A approves, R rejects, V requests revisions, E opens the canvas editor,
 * Esc closes. Shortcuts are ignored while a field has focus.
 */

const TYPE_ICON = { text: Type, image: ImageIcon, shape: Square, svg: Shapes, frame: ImageIcon };
const TYPE_LABEL = { text: 'Text', image: 'Image', shape: 'Shape', svg: 'Graphic', frame: 'Frame', qrcode: 'QR code', group: 'Group' };

const describeElement = (el) => {
  if (el.type === 'text') return String(el.text || '').replace(/\s+/g, ' ').slice(0, 64);
  if (el.type === 'image') return el.alt || 'Photograph';
  if (el.type === 'shape') return `${el.shape || 'rect'} · ${el.meta?.kind || 'shape'}`;
  return el.meta?.kind || el.type;
};

/**
 * The design at the largest size that fits its stage.
 *
 * Sized in script rather than with CSS `aspect-ratio`: a box that is capped on
 * BOTH axes stops honouring its ratio, and a preview that is even slightly
 * stretched is worse than useless for judging a layout.
 */
const Stage = ({ template, label }) => {
  const canvas = canvasOf(template);
  const page = pagesOf(template)[0];
  const holder = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = holder.current;
    if (!el) return undefined;
    const fit = () => {
      const scale = Math.min(el.clientWidth / canvas.width, el.clientHeight / canvas.height);
      setBox({ width: Math.floor(canvas.width * scale), height: Math.floor(canvas.height * scale) });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [canvas.width, canvas.height]);

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-2">
      {label && <p className="text-xs font-medium text-gray-300 truncate text-center px-2">{label}</p>}
      <div ref={holder} className="flex-1 min-h-0 flex items-center justify-center">
        {box.width > 0 && (
          <div className="relative bg-white shadow-2xl" style={{ width: box.width, height: box.height }} data-testid="review-stage">
            {page
              ? <PageThumbnailPreview page={page} canvasSize={canvas} fillContainer />
              : template.thumbnail && <img src={template.thumbnail} alt="" className="absolute inset-0 w-full h-full object-contain" />}
          </div>
        )}
      </div>
    </div>
  );
};

const Fact = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-1 text-xs">
    <dt className="text-gray-500 shrink-0">{label}</dt>
    <dd className="text-gray-900 text-right break-words min-w-0">{children}</dd>
  </div>
);

const Section = ({ title, count, children }) => (
  <section className="border-t border-gray-100 px-4 py-3">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
      {title}{count !== undefined && <span className="ml-1.5 text-gray-400 normal-case">{count}</span>}
    </h4>
    {children}
  </section>
);

const Score = ({ label, value, strong }) => (
  <div className="flex items-center gap-2 text-[11px]">
    <span className={`w-20 shrink-0 ${strong ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{label}</span>
    <span className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
      <span className={`block h-full rounded-full ${strong ? 'bg-orange-500' : 'bg-gray-400'}`} style={{ width: `${Math.round((value || 0) * 100)}%` }} />
    </span>
    <span className="w-9 text-right tabular-nums text-gray-700">{Math.round((value || 0) * 100)}%</span>
  </div>
);

const ReviewPreview = ({
  template, index, total, busy,
  onClose, onPrev, onNext,
  onApprove, onReject, onRevise, onPublish, onUnpublish, onSubmit,
  onEdit, onEditMeta, onDuplicate, onDelete, onOpenSimilar,
}) => {
  const [similar, setSimilar] = useState(null);
  const [compare, setCompare] = useState(null);
  const status = template.status || 'draft';
  const canvas = canvasOf(template);
  const elements = useMemo(() => elementsOf(template), [template]);
  const byType = useMemo(() => elements.reduce((m, el) => { m[el.type] = (m[el.type] || 0) + 1; return m; }, {}), [elements]);
  const v = template.variation;
  const flagged = template.similar_templates?.length || 0;

  useEffect(() => {
    setCompare(null);
    setSimilar(null);
    if (!flagged) return undefined;
    let cancelled = false;
    similarTemplates(template.id)
      .then((rows) => { if (!cancelled) setSimilar(rows); })
      .catch(() => { if (!cancelled) setSimilar([]); });
    return () => { cancelled = true; };
  }, [template.id, flagged]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"]')) return;       // a confirm dialog owns the keyboard
      const k = e.key.toLowerCase();
      if (e.key === 'Escape') { if (compare) setCompare(null); else onClose(); }
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
      else if (busy) return;
      else if (k === 'a' && canApprove(status)) onApprove(template);
      else if (k === 'r' && canReject(status)) onReject(template);
      else if (k === 'v' && canRevise(status)) onRevise(template);
      else if (k === 'e') onEdit(template);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [template, status, busy, compare, onClose, onPrev, onNext, onApprove, onReject, onRevise, onEdit]);

  // Portalled to <body>: inside the admin layout the fixed sidebar would sit
  // over the stage. z-50 matches the app's dialogs, which are appended later
  // and therefore still open above this.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-gray-900/95 flex" data-testid="review-preview">
      {/* ── stage ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="h-12 shrink-0 flex items-center justify-between px-4 text-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" onClick={onPrev} aria-label="Previous template" className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30" disabled={index <= 0}>
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-xs tabular-nums text-gray-400">{index + 1} / {total}</span>
            <button type="button" onClick={onNext} aria-label="Next template" className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30" disabled={index >= total - 1}>
              <ChevronRight className="w-5 h-5" />
            </button>
            {compare && (
              <button type="button" onClick={() => setCompare(null)} className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20">
                <Undo2 className="w-3.5 h-3.5" /> Exit comparison
              </button>
            )}
          </div>
          <p className="hidden lg:block text-[11px] text-gray-500">← → browse · A approve · R reject · V revisions · E edit · Esc close</p>
        </div>

        <div className="flex-1 min-h-0 flex items-stretch gap-6 px-6 pb-6">
          <Stage template={template} label={compare ? template.name : null} />
          {compare && <Stage template={compare.template} label={compare.template.name} />}
        </div>
      </div>

      {/* ── inspector ── */}
      <aside className="w-[360px] shrink-0 bg-white flex flex-col">
        <header className="px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1.5 ${STATUS_BADGE[status] || STATUS_BADGE.draft}`}>
              {STATUS_LABEL[status] || status}
            </span>
            <h3 className="text-base font-semibold text-gray-900 leading-snug break-words">{template.name}</h3>
            {(template.category || template.subcategory) && (
              <p className="text-xs text-gray-500 mt-0.5">{[template.category, template.subcategory].filter(Boolean).join(' › ')}</p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close preview" className="p-1 -mr-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </header>

        {/* decision */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {canPublish(status) ? (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 col-span-2" disabled={busy} onClick={() => onPublish(template)}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Globe className="w-4 h-4 mr-1.5" />} Publish to marketplace
            </Button>
          ) : (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" disabled={busy || !canApprove(status)} onClick={() => onApprove(template)}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />} Approve
            </Button>
          )}
          {!canPublish(status) && (
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-9" disabled={busy || !canReject(status)} onClick={() => onReject(template)}>
              <ThumbsDown className="w-4 h-4 mr-1.5" /> Reject
            </Button>
          )}
          {canRevise(status) && (
            <Button variant="outline" className="h-8 text-xs col-span-2" disabled={busy} onClick={() => onRevise(template)}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> Request revisions
            </Button>
          )}
          {canPublish(status) && (
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs" disabled={busy} onClick={() => onReject(template)}>
              <ThumbsDown className="w-3.5 h-3.5 mr-1.5" /> Reject
            </Button>
          )}
          {canUnpublish(status) && (
            <Button variant="outline" className="h-8 text-xs col-span-2" disabled={busy} onClick={() => onUnpublish(template)}>
              <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Unpublish
            </Button>
          )}
          {(status === 'rejected' || status === 'revision_requested' || status === 'draft') && (
            <Button variant="outline" className="h-8 text-xs col-span-2" disabled={busy} onClick={() => onSubmit(template)}>
              <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Return to pending review
            </Button>
          )}
          <Button variant="outline" className="h-8 text-xs" onClick={() => onEdit(template)}><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit canvas</Button>
          <Button variant="outline" className="h-8 text-xs" onClick={() => onEditMeta(template)}><Tags className="w-3.5 h-3.5 mr-1.5" /> Edit details</Button>
          <Button variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => onDuplicate(template)}><Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate</Button>
          <Button variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => onDelete(template)}><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {(template.rejection_reason || template.revision_notes) && (
            <Section title={status === 'rejected' ? 'Rejection reason' : 'Revision notes'}>
              <p className="text-xs text-gray-800 whitespace-pre-wrap">{template.rejection_reason || template.revision_notes}</p>
            </Section>
          )}

          <Section title="Details">
            <dl>
              <Fact label="Style">{template.style || '—'}</Fact>
              <Fact label="Dimensions">{canvas.width} × {canvas.height} px</Fact>
              <Fact label="Format">{template.template_format || '—'}</Fact>
              <Fact label="Pages">{pagesOf(template).length}</Fact>
              <Fact label="Elements">{elements.length}</Fact>
              <Fact label="Created">{formatDate(template.created_at)}</Fact>
              {template.library_id && <Fact label="Library id"><span className="font-mono text-[10px]">{template.library_id}</span></Fact>}
            </dl>
            {template.description && <p className="text-xs text-gray-600 mt-2 leading-relaxed">{template.description}</p>}
            {template.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {template.tags.map((tag) => <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tag}</span>)}
              </div>
            )}
          </Section>

          {v && (
            <Section title="Design variation">
              <dl>
                <Fact label="Layout">{v.composition_label || v.composition}</Fact>
                <Fact label="Art direction">{v.direction_label || v.direction}</Fact>
                <Fact label="Palette">{v.palette_name || v.palette}</Fact>
                <Fact label="Type">{v.pairing_label || v.pairing}</Fact>
                <Fact label="Photo cut">{[v.photo_shape, v.photo_frame !== 'none' && v.photo_frame].filter(Boolean).join(' · ')}</Fact>
                {v.photos?.length > 0 && <Fact label="Photography">{v.photos.map((ph) => ph.by).filter(Boolean).join(', ') || `${v.photos.length} photo(s)`} · Pexels</Fact>}
              </dl>
            </Section>
          )}

          <Section title="Similar templates" count={flagged || 0}>
            {!flagged && <p className="text-xs text-gray-500">No structurally similar template was flagged.</p>}
            {flagged > 0 && similar === null && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            {similar?.map(({ template: other, scores }) => (
              <div key={other.id} className={`rounded-lg border p-2 mb-2 ${compare?.template.id === other.id ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}>
                <div className="flex gap-2">
                  <div className="w-14 shrink-0 bg-gray-100 rounded overflow-hidden">
                    {pagesOf(other)[0] && <PageThumbnailPreview page={pagesOf(other)[0]} canvasSize={canvasOf(other)} fillWidth />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-900 truncate">{other.name}</p>
                    <p className="text-[10px] text-gray-500 truncate">{STATUS_LABEL[other.status] || other.status} · {other.style}</p>
                    <div className="flex gap-1 mt-1">
                      <button type="button" onClick={() => setCompare(compare?.template.id === other.id ? null : { template: other, scores })}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-900 text-white hover:bg-gray-700">
                        <GitCompare className="w-3 h-3" /> {compare?.template.id === other.id ? 'Hide' : 'Compare'}
                      </button>
                      <button type="button" onClick={() => onOpenSimilar(other)} className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-50">Open</button>
                    </div>
                  </div>
                </div>
                <div className="mt-2 space-y-0.5">
                  <Score label="Structure" value={scores.structural} strong />
                  <Score label="Layout" value={scores.layout} />
                  <Score label="Composition" value={scores.composition} />
                  <Score label="Typography" value={scores.typography} />
                  <Score label="Colour" value={scores.colour} />
                  <Score label="Assets" value={scores.assets} />
                </div>
              </div>
            ))}
            {flagged > 0 && similar?.length === 0 && <p className="text-xs text-gray-500">The flagged templates are no longer in the library.</p>}
          </Section>

          <Section title="Editable elements" count={elements.length}>
            <div className="flex flex-wrap gap-1 mb-2">
              {Object.entries(byType).map(([type, n]) => (
                <span key={type} className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{n} {TYPE_LABEL[type] || type}</span>
              ))}
            </div>
            <ol className="space-y-px" data-testid="review-elements">
              {elements.map((el, i) => {
                const Icon = TYPE_ICON[el.type] || Square;
                return (
                  <li key={el.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-gray-50">
                    <span className="w-5 text-right tabular-nums text-gray-400">{i + 1}</span>
                    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="truncate text-gray-800 flex-1" title={describeElement(el)}>{describeElement(el)}</span>
                    {el.type === 'text' && <span className="text-gray-400 shrink-0 tabular-nums">{el.fontSize}px</span>}
                  </li>
                );
              })}
            </ol>
          </Section>
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default ReviewPreview;
